/* eslint-disable jsx-a11y/media-has-caption */
import React, { useEffect, useRef, useState } from 'react';

import type { OptimizerProps } from './video_helpers.js';
import type {
  VideoAssetComponent,
  VideoImageAsset,
  Hotspot,
} from '../../../../../../core/data-sources/content-components/types.js';

import {
  getOptimalVideoVariant,
  getSubtitles,
  unique,
} from './video_helpers.js';
import { useHotspotStyles } from '../../../../hooks/useHotspot.js';

interface LoopingVideoAsset extends VideoAssetComponent {
  layout?: string;
  visible?: boolean;
  muted?: boolean;
  forcePreload?: boolean;
  externalClassName?: string;
  dynamicQuality?: boolean;
  imageAsset: VideoImageAsset;
  setTime?: (time: number) => void;
  presentationProperties?: Record<string, string>;
  hotspot?: Hotspot;
  hidePoster?: boolean;
}
const LoopingVideoAsset: React.FC<LoopingVideoAsset> = ({
  muted = true,
  visible = true,
  forcePreload = true,
  videoAsset: {
    size = { width: 100, height: 100 },
    metadata,
    streamUrls: { mp4 = '', pseudostreaming = [] },
    subtitles,
  },
  layout,
  dynamicQuality = true,
  imageAsset = {},
  setTime = () => undefined,
  hotspot,
  hidePoster = false,
}) => {
  const [optimalImage, setOptimalImage] = useState<string | undefined>();
  const [sources, setSources] = useState(
    unique([mp4, ...pseudostreaming]).filter(Boolean),
  );
  const hotspotStyles = useHotspotStyles({
    hotspot,
    size,
  });
  const videoElementRef = useRef<HTMLVideoElement>(null);

  // Pick the "optimal" preview image asset and video asset
  useEffect(() => {
    const optimizerConfig: OptimizerProps = {
      layout,
      cropFactor: 1,
    };
    const pseudostreamingUrls = [...pseudostreaming, mp4].filter(Boolean);
    const visibleHeight = videoElementRef?.current?.scrollHeight;
    const visibleWidth = videoElementRef?.current?.scrollWidth;
    const originalRatio = Number(metadata?.aspectRatio) || 1;

    if (dynamicQuality && visibleHeight && visibleWidth && originalRatio) {
      const expectedWidth = originalRatio * visibleHeight;
      /**
       * Estimate how much width of video is cropped away compared to if it
       * was shown in original aspect ratio. Ignore any "video should
       * actually have been smaller" results (< 1).
       */
      optimizerConfig.cropFactor = Math.max(expectedWidth / visibleWidth, 1);
      optimizerConfig.width = visibleWidth;
    }

    if (pseudostreamingUrls.length > 0) {
      const optimalVideo = getOptimalVideoVariant({
        ...optimizerConfig,
        urls: pseudostreamingUrls,
      });
      if (
        optimalVideo &&
        !pseudostreamingUrls.every((url) => url === optimalVideo)
      ) {
        setSources([optimalVideo]);
      }
      setOptimalImage(imageAsset.main);
    }
  }, [
    dynamicQuality,
    videoElementRef,
    metadata,
    pseudostreaming,
    mp4,
    imageAsset,
    layout,
  ]);

  useEffect(() => {
    if (visible) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      videoElementRef.current && videoElementRef.current.play();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      videoElementRef.current && videoElementRef.current.pause();
    }
  }, [visible]);

  if (sources.length < 1) {
    return null;
  }

  const handleTimeUpdate = () => {
    if (videoElementRef?.current?.currentTime) {
      setTime(videoElementRef?.current?.currentTime);
    }
  };

  const printpreloadLinks =
    forcePreload && sources.length === 1
      ? sources.map((url) => (
          <link
            key={`preload-link-${url}`}
            rel="prefetch"
            as="video"
            href={url}
          />
        ))
      : null;

  return (
    <>
      {printpreloadLinks}
      <video
        style={hotspotStyles}
        poster={hidePoster ? '' : optimalImage}
        preload={visible || forcePreload ? 'auto' : 'metadata'}
        muted={muted}
        autoPlay={visible}
        loop
        playsInline
        ref={videoElementRef}
        onTimeUpdate={handleTimeUpdate}
      >
        {visible || forcePreload
          ? sources.map((url) => (
              <source key={url} src={url} type="video/mp4" />
            ))
          : null}
        {subtitles && getSubtitles(subtitles, 'nb_NO') && (
          <track
            default
            kind="captions"
            srcLang={subtitles[0].language}
            src={subtitles[0].url}
          />
        )}
      </video>
    </>
  );
};

export default LoopingVideoAsset;
